import { css, html, LitElement, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { AllowedSocialMedia } from './types'

@safeCustomElement('mdc-social-media-stat-card')
export class MdcSocialMediaStatCard extends LitElement {
  @property({ type: String, attribute: 'social-media' }) socialMedia: AllowedSocialMedia =
    'instagram'
  @property({ type: String, attribute: 'social-media-link' }) socialMediaLink = ''
  @property({ type: String }) followers = ''

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
			--mdc-social-media-stat-card-icon-border: var(--color-functional-gray-200, #e5e5e5);
			--mdc-social-media-stat-card-icon-bg: var(--color-functional-gray-white, #ffffff);
			--mdc-social-media-stat-card-icon-color: #272727;
			--mdc-social-media-stat-card-font-family: var(--typography-font-family-sans);
			--mdc-social-media-stat-card-count-size: var(--text-087-and-half, 0.875rem);
			--mdc-social-media-stat-card-label-size: var(--text-068-and-three-quatars, 0.6875rem);
			--mdc-social-media-stat-card-count-color: var(--color-functional-gray-black, #111111);
			--mdc-social-media-stat-card-label-color: var(--color-functional-gray-400, #999999);
		}

		.mdc-social-media-stat-card p {
			margin: 0;
		}

		.mdc-social-media-stat-card {
			display: grid;
			align-items: center;
			grid-template-columns: auto 1fr;
			gap: 0.75rem;
		}

		.mdc-social-media-stat-card__icon {
			box-sizing: border-box;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 2.5rem;
			height: 2.5rem;
			border: 1px solid var(--mdc-social-media-stat-card-icon-border);
			border-radius: 100%;
			background-color: var(--mdc-social-media-stat-card-icon-bg);
			color: var(--mdc-social-media-stat-card-icon-color);
			aspect-ratio: 1 / 1;
			text-decoration: none;
		}

		.mdc-social-media-stat-card__icon > * {
			width: 50%;
			height: 50%;
		}

		.mdc-social-media-stat-card__followers {
			display: flex;
			flex-direction: column;
			font-family: var(--mdc-social-media-stat-card-font-family);
			font-weight: 450;
			line-height: 1.3;
			padding-right: 0.5rem;
			gap: 0.25rem;
		}

		.mdc-social-media-stat-card__followers-count {
			font-size: var(--mdc-social-media-stat-card-count-size);
			color: var(--mdc-social-media-stat-card-count-color);
		}

		.mdc-social-media-stat-card__followers-label {
			font-size: var(--mdc-social-media-stat-card-label-size);
			color: var(--mdc-social-media-stat-card-label-color);
		}
	`

  private getSocialMediaIcon() {
    switch (this.socialMedia) {
      case 'instagram':
        return html`
					<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path
							d="M12.0053 4.62102C14.4114 4.62102 14.6963 4.63157 15.6426 4.67376C16.522 4.71244 16.9969 4.86013 17.3135 4.9832C17.7321 5.14495 18.0346 5.34186 18.3477 5.65482C18.6643 5.97128 18.8577 6.27017 19.0195 6.68861C19.1427 7.00508 19.2904 7.4833 19.3291 8.35886C19.3713 9.30826 19.3819 9.59309 19.3819 11.9947C19.3819 14.3999 19.3713 14.6847 19.3291 15.6306C19.2904 16.5097 19.1427 16.9844 19.0195 17.3008C18.8577 17.7193 18.6607 18.0217 18.3477 18.3346C18.0311 18.6511 17.7321 18.8445 17.3135 19.0063C16.9969 19.1293 16.5185 19.277 15.6426 19.3157C14.6928 19.3579 14.4079 19.3684 12.0053 19.3684C9.59918 19.3684 9.31425 19.3579 8.36799 19.3157C7.48857 19.277 7.01368 19.1293 6.69709 19.0063C6.27848 18.8445 5.97596 18.6476 5.66289 18.3346C5.3463 18.0182 5.15282 17.7193 4.99101 17.3008C4.86789 16.9844 4.72015 16.5062 4.68145 15.6306C4.63924 14.6812 4.62869 14.3964 4.62869 11.9947C4.62869 9.58957 4.63924 9.30475 4.68145 8.35886C4.72015 7.47978 4.86789 7.00508 4.99101 6.68861C5.15282 6.27017 5.34981 5.96777 5.66289 5.65482C5.97948 5.33835 6.27848 5.14495 6.69709 4.9832C7.01368 4.86013 7.49209 4.71244 8.36799 4.67376C9.31425 4.63157 9.59918 4.62102 12.0053 4.62102ZM12.0053 3C9.56048 3 9.25445 3.01055 8.29412 3.05274C7.33731 3.09494 6.6795 3.24966 6.10963 3.47119C5.51515 3.70326 5.01212 4.00918 4.51261 4.51201C4.00958 5.01133 3.70354 5.51416 3.47137 6.1049C3.24976 6.67806 3.09498 7.3321 3.05277 8.28853C3.01055 9.252 3 9.55792 3 12.0018C3 14.4456 3.01055 14.7515 3.05277 15.7115C3.09498 16.6679 3.24976 17.3255 3.47137 17.8951C3.70354 18.4894 4.00958 18.9922 4.51261 19.4915C5.01212 19.9908 5.51515 20.3003 6.10612 20.5288C6.6795 20.7503 7.33379 20.9051 8.2906 20.9473C9.25093 20.9895 9.55697 21 12.0018 21C14.4466 21 14.7526 20.9895 15.7129 20.9473C16.6697 20.9051 17.3275 20.7503 17.8974 20.5288C18.4884 20.3003 18.9914 19.9908 19.4909 19.4915C19.9904 18.9922 20.3 18.4894 20.5286 17.8986C20.7502 17.3255 20.905 16.6714 20.9472 15.715C20.9894 14.755 21 14.4491 21 12.0053C21 9.56144 20.9894 9.25552 20.9472 8.29557C20.905 7.33913 20.7502 6.68158 20.5286 6.11194C20.307 5.51416 20.001 5.01133 19.4979 4.51201C18.9984 4.0127 18.4954 3.70326 17.9044 3.4747C17.3311 3.25317 16.6768 3.09846 15.72 3.05626C14.7561 3.01055 14.4501 3 12.0053 3Z"
							fill="currentColor"
						/>
						<path
							d="M11.9998 7.5C9.41604 7.5 7.31982 9.51559 7.31982 12C7.31982 14.4844 9.41604 16.5 11.9998 16.5C14.5836 16.5 16.6798 14.4844 16.6798 12C16.6798 9.51559 14.5836 7.5 11.9998 7.5ZM11.9998 14.919C10.3236 14.919 8.96405 13.6118 8.96405 12C8.96405 10.3882 10.3236 9.08099 11.9998 9.08099C13.6761 9.08099 15.0356 10.3882 15.0356 12C15.0356 13.6118 13.6761 14.919 11.9998 14.919Z"
							fill="currentColor"
						/>
						<path
							d="M18.1195 7.12646C18.1195 7.74943 17.634 8.25146 17.0395 8.25146C16.4414 8.25146 15.9595 7.74576 15.9595 7.12646C15.9595 6.5035 16.4449 6.00146 17.0395 6.00146C17.634 6.00146 18.1195 6.50717 18.1195 7.12646Z"
							fill="currentColor"
						/>
					</svg>
				`
      case 'tiktok':
        return html`
					<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path
							d="M15.5801 3H12.4726V15.2608C12.4726 16.7218 11.2774 17.9218 9.79006 17.9218C8.3027 17.9218 7.10751 16.7218 7.10751 15.2608C7.10751 13.8261 8.27614 12.6522 9.71039 12.6V9.52175C6.54975 9.5739 4 12.1044 4 15.2608C4 18.4435 6.60287 21 9.81663 21C13.0303 21 15.6332 18.4174 15.6332 15.2608V8.9739C16.8019 9.8087 18.2361 10.3043 19.75 10.3305V7.25217C17.4128 7.17391 15.5801 5.29565 15.5801 3Z"
							fill="currentColor"
						/>
					</svg>
				`
      default:
        return nothing
    }
  }

  render() {
    return html`
			<div class="mdc-social-media-stat-card">
				<a href=${this.socialMediaLink} target="_blank" rel="noopener noreferrer" class="mdc-social-media-stat-card__icon">
					${this.getSocialMediaIcon()}
				</a>
				<div class="mdc-social-media-stat-card__followers">
					<p class="mdc-social-media-stat-card__followers-count">${this.followers}</p>
					<p class="mdc-social-media-stat-card__followers-label">Followers</p>
				</div>
			</div>
		`
  }
}
